import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAthlete } from '@/context/AthleteContext';
import { Lock, User, Mail, Eye, EyeOff, Save, Shield, Edit2, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentSettings = () => {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const { athlete, refreshAthlete } = useAthlete();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile edit state
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    managed_by: 'parent' // 'parent' or 'athlete'
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Athlete data state
  const [athleteData, setAthleteData] = useState({
    name: '',
    birth_date: ''
  });
  const [savingAthlete, setSavingAthlete] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Initialize profile data from user
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        managed_by: user.managed_by || 'parent'
      });
    }
  }, [user]);

  // Initialize athlete data
  useEffect(() => {
    if (athlete) {
      setAthleteData({
        name: athlete.name || '',
        birth_date: athlete.birth_date || ''
      });
    }
  }, [athlete]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/users/profile`, profileData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Profile updated successfully!');
      setIsEditing(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
    setSavingProfile(false);
  };

  const handleSaveAthlete = async () => {
    setSavingAthlete(true);
    try {
      await axios.put(`${API}/athletes/${athlete.id}`, athleteData, {
        headers: getAuthHeaders()
      });
      
      toast.success('Athlete information updated!');
      if (refreshAthlete) refreshAthlete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update athlete info');
    }
    setSavingAthlete(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSavingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, null, {
        params: {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        headers: getAuthHeaders()
      });
      
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
    setSavingPassword(false);
  };

  return (
    <div className="space-y-6" data-testid="parent-settings">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border/40 pb-2">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
            activeTab === 'profile' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-muted-foreground hover:text-white'
          }`}
        >
          <User size={16} className="inline mr-2" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('athlete')}
          className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
            activeTab === 'athlete' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-muted-foreground hover:text-white'
          }`}
        >
          <Users size={16} className="inline mr-2" />
          Athlete
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
            activeTab === 'security' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-muted-foreground hover:text-white'
          }`}
        >
          <Shield size={16} className="inline mr-2" />
          Security
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-card border border-border/40 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg uppercase">Account Information</h2>
            {!isEditing && (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 size={14} className="mr-2" />Edit
              </Button>
            )}
          </div>
          
          <div className="space-y-4 max-w-md">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Full Name</label>
              {isEditing ? (
                <Input
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  className="bg-transparent"
                  placeholder="Your full name"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  <User size={18} className="text-muted-foreground" />
                  <span>{user?.full_name || 'N/A'}</span>
                </div>
              )}
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Email</label>
              {isEditing ? (
                <Input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  className="bg-transparent"
                  placeholder="your.email@example.com"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  <Mail size={18} className="text-muted-foreground" />
                  <span>{user?.email || 'N/A'}</span>
                </div>
              )}
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Profile Managed By</label>
              {isEditing ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => setProfileData({...profileData, managed_by: 'parent'})}
                    className={`flex-1 p-3 rounded-sm border transition-colors ${
                      profileData.managed_by === 'parent'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/40 hover:border-border'
                    }`}
                  >
                    <Users size={18} className="inline mr-2" />
                    Parent
                  </button>
                  <button
                    onClick={() => setProfileData({...profileData, managed_by: 'athlete'})}
                    className={`flex-1 p-3 rounded-sm border transition-colors ${
                      profileData.managed_by === 'athlete'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/40 hover:border-border'
                    }`}
                  >
                    <User size={18} className="inline mr-2" />
                    Athlete
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                  <Users size={18} className="text-muted-foreground" />
                  <span className="capitalize">{user?.managed_by || 'Parent'}</span>
                </div>
              )}
            </div>
            
            {isEditing && (
              <div className="flex gap-3 pt-4">
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-primary text-black">
                  <Save size={16} className="mr-2" />
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Athlete Tab */}
      {activeTab === 'athlete' && athlete && (
        <div className="bg-card border border-border/40 rounded-sm p-6">
          <h2 className="font-heading text-lg uppercase mb-4">Athlete Information</h2>
          
          <div className="space-y-4 max-w-md">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Athlete Name</label>
              <Input
                value={athleteData.name}
                onChange={(e) => setAthleteData({...athleteData, name: e.target.value})}
                className="bg-transparent"
                placeholder="Athlete's full name"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Date of Birth</label>
              <div className="relative">
                <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={athleteData.birth_date}
                  onChange={(e) => setAthleteData({...athleteData, birth_date: e.target.value})}
                  className="bg-transparent pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This is part of the athlete's unique identity
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Position</label>
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-sm">
                <span>{athlete.position || 'Not set'}</span>
              </div>
            </div>
            
            <Button onClick={handleSaveAthlete} disabled={savingAthlete} className="bg-primary text-black">
              <Save size={16} className="mr-2" />
              {savingAthlete ? 'Saving...' : 'Save Athlete Info'}
            </Button>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="bg-card border border-border/40 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-primary" size={22} />
            <div>
              <h2 className="font-heading text-lg uppercase">Change Password</h2>
              <p className="text-sm text-muted-foreground">Update your account password</p>
            </div>
          </div>
          
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Current Password</label>
              <div className="relative">
                <Input
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  required
                  className="bg-transparent pr-10"
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                >
                  {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">New Password</label>
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                required
                minLength={6}
                className="bg-transparent"
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Confirm New Password</label>
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                required
                minLength={6}
                className="bg-transparent"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={savingPassword} 
              className="bg-primary text-black"
              data-testid="change-password-btn"
            >
              <Save size={16} className="mr-2" />
              {savingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ParentSettings;
